import {
  addSupplierFoodAdmin,
  addSupplierSnackAdmin,
  removeSupplierFoodAdmin,
  removeSupplierSnackAdmin,
  supplierFoodAdminsOptions,
  supplierSnackAdminsOptions,
  useBuilding,
} from "@/api";
import AddUserToRoleModal from "@/components/AddUserToRoleModal";
import { useBuildingQuery } from "@/components/RouterSetup";
import Table from "@/components/table/Table.tsx";
import { getBuildingId } from "@/lib";
import styles from "@/styles/AdminsSection.module.scss";
import { Building, ExtendedUser, NotificationType, User } from "@/types";
import { useSnackbarStore } from "@/store/zustandStore";
import { Button, Icon } from "@app-components";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnHelper<ExtendedUser>();

type SupplierAdminRole = "snack" | "food";

/**
 * Config per role so the same table/add/remove UI serves both the
 * SupplierSnackAdmin and SupplierFoodAdmin lists. Kept in one place so a future
 * CompanyFoodAdmin role can be added the same way.
 */
const ROLE_CONFIG: Record<
  SupplierAdminRole,
  {
    buildingField: "supplierSnackAdmins" | "supplierFoodAdmins";
    queryKey: string;
    options: () => ReturnType<typeof supplierSnackAdminsOptions>;
    addAdmin: (userId: string) => Promise<unknown>;
    removeAdmin: (userId: string) => Promise<unknown>;
    keys: {
      header: string;
      description: string;
      addButton: string;
      noData: string;
      modalHeader: string;
      modalBody: string;
      modalNoResults: string;
      alreadyExists: string;
      addSuccess: string;
      addError: string;
      removeSuccess: string;
      removeError: string;
    };
  }
> = {
  snack: {
    buildingField: "supplierSnackAdmins",
    queryKey: "supplier-snack-admins",
    options: supplierSnackAdminsOptions,
    addAdmin: addSupplierSnackAdmin,
    removeAdmin: removeSupplierSnackAdmin,
    keys: {
      header: "SNACK_ADMIN.SETTINGS.ADMINS.HEADER",
      description: "SNACK_ADMIN.SETTINGS.ADMINS.DESCRIPTION",
      addButton: "SNACK_ADMIN.SETTINGS.ADMINS.ADD",
      noData: "SNACK_ADMIN.SETTINGS.ADMINS.NO_DATA",
      modalHeader: "SNACK_ADMIN.SETTINGS.ADMINS.ADD_MODAL.HEADER",
      modalBody: "SNACK_ADMIN.SETTINGS.ADMINS.ADD_MODAL.BODY",
      modalNoResults: "SNACK_ADMIN.SETTINGS.ADMINS.ADD_MODAL.NO_RESULTS",
      alreadyExists: "SNACK_ADMIN.SETTINGS.ADMINS.ADD_MODAL.ALREADY_ADMIN",
      addSuccess: "SNACK_ADMIN.SETTINGS.ADMINS.ADD_MODAL.SUCCESS",
      addError: "SNACK_ADMIN.SETTINGS.ADMINS.ADD_MODAL.ERROR",
      removeSuccess: "SNACK_ADMIN.SETTINGS.ADMINS.REMOVE_SUCCESS",
      removeError: "SNACK_ADMIN.SETTINGS.ADMINS.REMOVE_ERROR",
    },
  },
  food: {
    buildingField: "supplierFoodAdmins",
    queryKey: "supplier-food-admins",
    options: supplierFoodAdminsOptions,
    addAdmin: addSupplierFoodAdmin,
    removeAdmin: removeSupplierFoodAdmin,
    keys: {
      header: "FOOD_ADMIN.SETTINGS.ADMINS.HEADER",
      description: "FOOD_ADMIN.SETTINGS.ADMINS.DESCRIPTION",
      addButton: "FOOD_ADMIN.SETTINGS.ADMINS.ADD",
      noData: "FOOD_ADMIN.SETTINGS.ADMINS.NO_DATA",
      modalHeader: "FOOD_ADMIN.SETTINGS.ADMINS.ADD_MODAL.HEADER",
      modalBody: "FOOD_ADMIN.SETTINGS.ADMINS.ADD_MODAL.BODY",
      modalNoResults: "FOOD_ADMIN.SETTINGS.ADMINS.ADD_MODAL.NO_RESULTS",
      alreadyExists: "FOOD_ADMIN.SETTINGS.ADMINS.ADD_MODAL.ALREADY_ADMIN",
      addSuccess: "FOOD_ADMIN.SETTINGS.ADMINS.ADD_MODAL.SUCCESS",
      addError: "FOOD_ADMIN.SETTINGS.ADMINS.ADD_MODAL.ERROR",
      removeSuccess: "FOOD_ADMIN.SETTINGS.ADMINS.REMOVE_SUCCESS",
      removeError: "FOOD_ADMIN.SETTINGS.ADMINS.REMOVE_ERROR",
    },
  },
};

/** Update the cached building doc's admin array without a refetch. */
const patchBuildingAdmins = (
  queryClient: ReturnType<typeof useQueryClient>,
  field: "supplierSnackAdmins" | "supplierFoodAdmins",
  updater: (current: string[]) => string[],
) => {
  queryClient.setQueryData(
    [getBuildingId(), "building"],
    (current: Building | undefined) =>
      current
        ? { ...current, [field]: updater(current[field] ?? []) }
        : current,
  );
};

export default function SupplierAdminsSection({
  role,
}: {
  role: SupplierAdminRole;
}) {
  const { t } = useTranslation();
  const config = ROLE_CONFIG[role];
  const queryClient = useQueryClient();
  const { addNotification } = useSnackbarStore();
  const { data: building } = useBuilding();
  const { isLoading, data: admins } = useBuildingQuery<ExtendedUser[]>(
    config.options(),
  );
  const [addAdminOpen, setAddAdminOpen] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [getBuildingId(), "building"],
    });
    queryClient.invalidateQueries({
      queryKey: [getBuildingId(), config.queryKey],
    });
  };

  const removeAdmin = async (e: Event, userId: string) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await config.removeAdmin(userId);
      patchBuildingAdmins(queryClient, config.buildingField, (ids) =>
        ids.filter((id) => id !== userId),
      );
      invalidate();
      addNotification(t(config.keys.removeSuccess), NotificationType.SUCCESS);
    } catch (error) {
      console.error(error);
      addNotification(t(config.keys.removeError));
    }
  };

  const tableColumns = useMemo(
    () => [
      columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
        id: "userName",
        cell: (info) => info.getValue(),
        header: () => <div>{t("USERS.TABLE_HEADERS.NAME")}</div>,
      }),
      columnHelper.accessor("email", {
        cell: (info) => info.getValue(),
        header: () => <div>{t("USERS.TABLE_HEADERS.EMAIL")}</div>,
      }),
      columnHelper.accessor("phone", {
        cell: (info) => info.getValue(),
        header: () => <div>{t("USERS.TABLE_HEADERS.PHONE")}</div>,
      }),
      columnHelper.accessor("buildingTenantName", {
        cell: (info) => info.getValue(),
        header: () => <div>{t("USERS.TABLE_HEADERS.COMPANY")}</div>,
      }),
      columnHelper.accessor((row) => row, {
        id: "actions",
        cell: (info) => {
          const { userId } = info.getValue();
          return (
            <div className="rowActions">
              <Button
                size="sm"
                danger
                onClick={(e) => removeAdmin(e, userId)}
                style={{ transition: "background-color 0.3s ease" }}
              >
                <Icon name="user_cogwheel" />
                <span className="sr-only">
                  {t("BUILDING_SETTINGS.ADMINS.REMOVE_AS_ADMIN")}
                </span>
              </Button>
            </div>
          );
        },
        header: () => (
          <div className="sr-only">{t("COMPANY_SETTINGS.ADMINS.ACTIONS")}</div>
        ),
      }),
    ],
    [t],
  );

  return (
    <>
      <section
        className={`${styles.adminsTable} ${styles.noFirstColStyle}`}
        style={{ display: "block", marginTop: "var(--wlcm-spacing-md)" }}
      >
        <div className={`${styles.fullWidth} ${styles.sectionWithButton}`}>
          <h3>{t(config.keys.header)}</h3>
          <p className={styles.description}>{t(config.keys.description)}</p>
          <Button
            icon="add"
            onClick={(e) => {
              e.preventDefault();
              setAddAdminOpen(true);
            }}
          >
            {t(config.keys.addButton)}
          </Button>
        </div>
        <Table
          name={config.queryKey}
          isLoading={isLoading}
          data={admins ?? []}
          columns={tableColumns}
          className={styles.fullWidth}
          noDataMessage={t(config.keys.noData)}
        />
      </section>
      {/* createPortal is REQUIRED so the modal form is not rendered inside the settings <form> */}
      {createPortal(
        <AddUserToRoleModal
          open={addAdminOpen}
          header={t(config.keys.modalHeader)}
          body={t(config.keys.modalBody)}
          noResultsText={t(config.keys.modalNoResults)}
          existingAdmins={admins ?? []}
          searchCompanyId={building.supplierCompanyId}
          onClose={() => setAddAdminOpen(false)}
          onAdd={async (userToAdd: User) => {
            await config.addAdmin(userToAdd.userId);
            patchBuildingAdmins(queryClient, config.buildingField, (ids) => [
              ...new Set([...ids, userToAdd.userId]),
            ]);
            invalidate();
          }}
          alreadyExistsMessageKey={config.keys.alreadyExists}
          successMessageKey={config.keys.addSuccess}
          errorMessageKey={config.keys.addError}
        />,
        document.querySelector("main") || document.body,
      )}
    </>
  );
}
